import { useEffect, useState, forwardRef } from "react";
import { Product } from "@/types/stock";
import { generateBarcodeDataURL } from "@/utils/barcodeUtils";

interface BarcodeStickerProps {
  product: Product;
  barcode?: string;
  className?: string;
  width?: number;
  height?: number;
  template?: "compact" | "detailed";
}

/**
 * BarcodeSticker component for generating printable product stickers
 * Contains product name, EAN13 barcode, and price in a standard sticker format
 */
const BarcodeSticker = forwardRef<HTMLDivElement, BarcodeStickerProps>(
  (
    {
      product,
      barcode,
      className = "",
      width = 40,
      height = 20,
      template = "compact",
    },
    ref
  ) => {
    const [barcodeDataURL, setBarcodeDataURL] = useState<string>("");
    const [currentBarcode, setCurrentBarcode] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>("");

    useEffect(() => {
      const generateBarcode = async () => {
        try {
          setLoading(true);
          setError("");

          // Determine which barcode to use
          const barcodeToUse = barcode || product.barcode;

          if (!barcodeToUse) {
            setError("No barcode available");
            setLoading(false);
            return;
          }

          // Set the current barcode
          setCurrentBarcode(barcodeToUse);

          // Generate barcode image optimized for high quality
          const dataURL = await generateBarcodeDataURL(barcodeToUse, {
            format: "EAN13", // Try EAN13 first
            width: 3, // Thicker bars for better quality
            height: 80, // Taller for better scanning
            displayValue: true,
            fontSize: 16,
            textMargin: 5,
            margin: 10,
            background: "#ffffff",
            lineColor: "#000000",
          });

          setBarcodeDataURL(dataURL);
        } catch (err) {
          console.error("Error generating barcode:", err);

          // If EAN13 fails, try CODE128 which is more flexible
          try {
            const barcodeToUse = barcode || product.barcode;
            if (barcodeToUse) {
              const dataURL = await generateBarcodeDataURL(barcodeToUse, {
                format: "CODE128",
                width: 3,
                height: 80,
                displayValue: true,
                fontSize: 16,
                textMargin: 5,
                margin: 10,
                background: "#ffffff",
                lineColor: "#000000",
              });
              setBarcodeDataURL(dataURL);
            }
          } catch (fallbackErr) {
            console.error("Error generating fallback barcode:", fallbackErr);
            setError("Failed to generate barcode");
          }
        } finally {
          setLoading(false);
        }
      };

      generateBarcode();
    }, [product.id, product.barcode, barcode]);

    if (loading) {
      return (
        <div
          ref={ref}
          className={`bg-white border border-gray-300 p-4 rounded-lg ${className}`}
        >
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded mb-2"></div>
            <div className="h-16 bg-gray-200 rounded mb-2"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div
          ref={ref}
          className={`bg-white border border-red-300 p-4 rounded-lg ${className}`}
        >
          <div className="text-red-600 text-center">
            <p className="text-sm">{error}</p>
          </div>
        </div>
      );
    }

    const isCompact = template === "compact";
    const fontSize = Math.max(6, Math.min(14, height * 0.35));
    const barcodeHeight = height * 0.4;

    // Inline print styles to ensure zero margins/padding when printing
    const printStyles = `
      @media print {
        .barcode-sticker-container {
          margin: 0 !important;
          padding: 0 !important;
          border: none !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          page-break-before: avoid !important;
          page-break-after: avoid !important;
          page-break-inside: avoid !important;
          overflow: hidden !important;
        }
      }
    `;

    return (
      <>
        <style>{printStyles}</style>
        <div
          ref={ref}
          className={`barcode-sticker-container bg-white border border-gray-300 p-1 rounded-lg ${className}`}
          style={{
            width: `${width}mm`,
            height: `${height}mm`,
            fontSize: `${fontSize}px`,
            lineHeight: "1.1",
            padding: "1mm",
          }}
        >
          {/* Product Name */}
          <div className="text-center mb-1">
            <h3
              className="font-bold text-black leading-tight"
              style={{
                fontSize: `${fontSize}px`,
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: isCompact ? 1 : 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {product.name}
            </h3>
          </div>

          {/* SKU - Only in detailed template */}
          {!isCompact && (
            <div className="text-center mb-1">
              <p
                className="text-gray-600"
                style={{ fontSize: `${fontSize * 0.85}px` }}
              >
                SKU: {product.sku}
              </p>
            </div>
          )}

          {/* Barcode */}
          <div className="flex justify-center mb-1">
            {barcodeDataURL ? (
              <img
                src={barcodeDataURL}
                alt={`Barcode: ${currentBarcode}`}
                className="max-w-full h-auto"
                style={{
                  maxHeight: `${barcodeHeight}mm`,
                  width: "auto",
                  maxWidth: `${width - 4}mm`,
                }}
              />
            ) : (
              <div className="h-6 bg-gray-200 rounded flex items-center justify-center text-xs">
                <span className="text-gray-500" style={{ fontSize: "5px" }}>
                  No barcode
                </span>
              </div>
            )}
          </div>

          {/* Price */}
          <div className="text-center">
            <div
              className="font-bold text-green-600"
              style={{ fontSize: `${fontSize}px` }}
            >
              {product.price.toFixed(3)} TND
            </div>
          </div>

          {/* Stock - Only in detailed template */}
          {!isCompact && (
            <div className="text-center">
              <p
                className="text-gray-600"
                style={{ fontSize: `${fontSize * 0.85}px` }}
              >
                Stock: {product.stockQuantity}
              </p>
            </div>
          )}
        </div>
      </>
    );
  }
);

BarcodeSticker.displayName = "BarcodeSticker";

export default BarcodeSticker;
